import { StackClientApp } from '@stackframe/react';
import { useNavigate } from 'react-router-dom';

/**
 * Neon Auth (Stack) Client Configuration
 *
 * This file initializes the Neon Auth SDK for client-side authentication.
 * The SDK handles:
 * - User authentication (email/password, OAuth)
 * - Session management
 * - Token storage in cookies
 * - User profile management
 * - Team management
 *
 * Environment variables required:
 * - VITE_NEON_AUTH_ENABLED: Enable/disable Neon Auth (true/false)
 * - VITE_NEON_AUTH_PROJECT_ID: Your Neon Auth project ID
 * - VITE_NEON_AUTH_PUBLISHABLE_KEY: Your publishable client key
 *
 * SECURITY NOTE:
 * All VITE_* environment variables are PUBLIC and exposed to the client by design.
 * Vite performs static replacement at build time (both client and SSR builds).
 * NEVER put sensitive secrets in VITE_* variables - only use for public configuration.
 * For sensitive server-side values, use non-VITE_ prefixed environment variables
 * that are only accessible to the backend.
 */

// Check if Neon Auth is enabled
// This value is statically replaced at build time for both client and SSR bundles
export const isNeonAuthEnabled =
  import.meta.env.VITE_NEON_AUTH_ENABLED === 'true';

// Create the Stack client app instance
// This will be null if Neon Auth is not enabled or if running on the server
// Guard against SSR by checking if window is defined
export const stackClientApp =
  isNeonAuthEnabled && typeof window !== 'undefined'
    ? new StackClientApp({
        projectId: import.meta.env.VITE_NEON_AUTH_PROJECT_ID,
        publishableClientKey: import.meta.env.VITE_NEON_AUTH_PUBLISHABLE_KEY,
        tokenStore: 'cookie', // Store tokens in cookies (accessible by SDK)
        redirectMethod: { useNavigate }, // Use React Router for navigation
        urls: {
          signIn: '/handler/sign-in',
          afterSignIn: '/',
          afterSignOut: '/handler/sign-in',
        },
      })
    : null;

/**
 * Helper function to get the access token for API requests
 * Returns null if Neon Auth is not enabled or user is not authenticated
 */
export const getNeonAuthAccessToken = async (): Promise<string | null> => {
  if (!stackClientApp) {
    console.warn('[getNeonAuthAccessToken] Stack client app not initialized');
    return null;
  }

  try {
    const user = await stackClientApp.getUser();
    if (!user) {
      console.warn('[getNeonAuthAccessToken] No user found');
      return null;
    }

    const authJson = await user.getAuthJson();
    const token = authJson?.accessToken;

    if (!token) {
      console.error(
        '[getNeonAuthAccessToken] No access token in authJson:',
        authJson
      );
      return null;
    }

    console.log(
      '[getNeonAuthAccessToken] Token retrieved successfully, length:',
      token.length
    );
    return token;
  } catch (error) {
    console.error(
      '[getNeonAuthAccessToken] Failed to get access token:',
      error
    );
    return null;
  }
};
